import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';

import { apiDelete, apiGet, apiPath, apiPost, apiPut } from '@/lib/api/client';
import { CubixApiError } from '@/lib/api/types';

import type {
  CreateRoleRequest,
  PermissionsMatrix,
  RoleEntry,
  UpdateRoleRequest,
} from './types';

const KEY = ['permissions', 'matrix'] as const;

/** GET /api/v1/permissions — full matrix + catalog. */
export function usePermissionsMatrix(
  options?: Omit<UseQueryOptions<PermissionsMatrix, CubixApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: KEY,
    queryFn: () => apiGet<PermissionsMatrix>(apiPath('permissions')),
    ...options,
  });
}

interface ReplaceRoleArgs {
  role: string;
  permissionKeys: string[];
}

/** PUT /api/v1/permissions/role/{role} — bulk replace grants for a role. */
export function useReplaceRole(
  options?: UseMutationOptions<string[], CubixApiError, ReplaceRoleArgs>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ role, permissionKeys }) =>
      apiPut<string[], string[]>(
        apiPath('permissions', 'role', role),
        permissionKeys),
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: KEY });
      return options?.onSuccess?.(...args);
    },
  });
}

/** POST /api/v1/permissions/role/{role}/reset — reset to platform defaults (system roles only). */
export function useResetRole(
  options?: UseMutationOptions<string[], CubixApiError, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (role) =>
      apiPost<string[], undefined>(
        apiPath('permissions', 'role', role, 'reset'), undefined),
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: KEY });
      return options?.onSuccess?.(...args);
    },
  });
}

// ── Dynamic Roles (1.28.0) ──────────────────────────────────────────

/** POST /api/v1/permissions/roles — create a custom role. */
export function useCreateRole(
  options?: UseMutationOptions<RoleEntry, CubixApiError, CreateRoleRequest>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiPost<RoleEntry, CreateRoleRequest>(apiPath('permissions', 'roles'), body),
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: KEY });
      return options?.onSuccess?.(...args);
    },
  });
}

interface UpdateRoleArgs {
  role: string;
  body: UpdateRoleRequest;
}

/** PUT /api/v1/permissions/roles/{role} — rename + update description (custom roles only). */
export function useUpdateRole(
  options?: UseMutationOptions<RoleEntry, CubixApiError, UpdateRoleArgs>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ role, body }) =>
      apiPut<RoleEntry, UpdateRoleRequest>(apiPath('permissions', 'roles', role), body),
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: KEY });
      return options?.onSuccess?.(...args);
    },
  });
}

/** DELETE /api/v1/permissions/roles/{role} — delete custom role (refused if assigned to users). */
export function useDeleteRole(
  options?: UseMutationOptions<void, CubixApiError, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (role) => apiDelete<void>(apiPath('permissions', 'roles', role)),
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: KEY });
      return options?.onSuccess?.(...args);
    },
  });
}
